// ============================================================
// TaskFlow OS - Dashboard Page
// ============================================================

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Header from '../components/layout/Header';
import { useAuthStore } from '../stores/authStore';
import { useProjectStore } from '../stores/projectStore';
import { supabase } from '../lib/supabase';
import type { EventLogEntry } from '../lib/types';
import { EVENT_TYPE_LABELS } from '../lib/constants';
import {
  IconProject, IconWorkflow, IconCheck, IconClock,
  IconActivity, IconBarChart, IconPlus, IconCpu,
} from '../icons/SvgIcons';

interface DashboardStats {
  totalProjects: number;
  activeWorkflows: number;
  completedTasks: number;
  pendingApprovals: number;
}

const StatCard: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: number | string;
  color: string;
  delay?: number;
}> = ({ icon, label, value, color, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay }}
    className="glass-card group"
  >
    <div className="flex items-center justify-between mb-3">
      <div
        className="w-10 h-10 rounded-xl flex items-center justify-center"
        style={{ background: `${color}15`, border: `1px solid ${color}25` }}
      >
        {icon}
      </div>
      <IconBarChart size={16} className="text-white/20 group-hover:text-white/40 transition-colors" />
    </div>
    <p className="text-2xl font-bold text-white">{value}</p>
    <p className="text-xs text-white/40 mt-1">{label}</p>
  </motion.div>
);

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [stats, setStats] = useState<DashboardStats>({
    totalProjects: 0,
    activeWorkflows: 0,
    completedTasks: 0,
    pendingApprovals: 0,
  });
  const [recentEvents, setRecentEvents] = useState<EventLogEntry[]>([]);

  useEffect(() => {
    const fetchStats = async () => {
      if (!user) return;

      const [projectsRes, workflowsRes, completedRes, approvalsRes] = await Promise.all([
        supabase.from('projects').select('id', { count: 'exact', head: true }).is('archived_at', null),
        supabase.from('workflows').select('id', { count: 'exact', head: true }),
        supabase.from('operational_nodes').select('id', { count: 'exact', head: true }).eq('status', 'completed'),
        supabase.from('approval_nodes').select('id', { count: 'exact', head: true }).eq('approval_status', 'pending'),
      ]);

      setStats({
        totalProjects: projectsRes.count || 0,
        activeWorkflows: workflowsRes.count || 0,
        completedTasks: completedRes.count || 0,
        pendingApprovals: approvalsRes.count || 0,
      });
    };

    const fetchRecentEvents = async () => {
      const { data } = await supabase
        .from('event_log')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(10);

      if (data) setRecentEvents(data as EventLogEntry[]);
    };

    fetchStats();
    fetchRecentEvents();
  }, [user]);

  return (
    <>
      <Header
        title={`おかえりなさい、${user?.display_name || 'ユーザー'}`}
        subtitle="今日のタスク状況を確認しましょう"
        actions={
          <button
            onClick={() => navigate('/projects')}
            className="glass-btn-primary flex items-center gap-2 text-sm"
          >
            <IconPlus size={16} />
            新規プロジェクト
          </button>
        }
      />

      <div className="p-6 space-y-6">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            icon={<IconProject size={20} color="#10b981" />}
            label="プロジェクト数"
            value={stats.totalProjects}
            color="#10b981"
            delay={0}
          />
          <StatCard
            icon={<IconWorkflow size={20} color="#3b82f6" />}
            label="稼働ワークフロー"
            value={stats.activeWorkflows}
            color="#3b82f6"
            delay={0.1}
          />
          <StatCard
            icon={<IconCheck size={20} color="#06b6d4" />}
            label="完了タスク"
            value={stats.completedTasks}
            color="#06b6d4"
            delay={0.2}
          />
          <StatCard
            icon={<IconClock size={20} color="#f59e0b" />}
            label="承認待ち"
            value={stats.pendingApprovals}
            color="#f59e0b"
            delay={0.3}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Recent Activity */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="lg:col-span-2 glass-card"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                <IconActivity size={16} color="#10b981" />
                最近のアクティビティ
              </h3>
              <button
                onClick={() => navigate('/activity')}
                className="text-xs text-emerald-400 hover:text-emerald-300 transition-colors"
              >
                すべて表示
              </button>
            </div>

            <div className="space-y-1">
              {recentEvents.length === 0 ? (
                <p className="text-sm text-white/30 text-center py-8">
                  まだアクティビティはありません
                </p>
              ) : (
                recentEvents.map((event, idx) => (
                  <div
                    key={event.id}
                    className="flex items-start gap-3 p-3 rounded-xl hover:bg-white/5 transition-colors"
                  >
                    <div className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center shrink-0 mt-0.5">
                      <IconActivity size={14} className="text-white/40" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-white/80">
                        <span className="font-medium text-white">{event.actor_display_name || '不明'}</span>
                        {' が '}
                        <span className="text-emerald-400/80">{EVENT_TYPE_LABELS[event.event_type] || event.event_type}</span>
                        {' を実行'}
                      </p>
                      {event.payload && Object.keys(event.payload).length > 0 && (
                        <p className="text-[10px] text-white/30 mt-0.5 truncate">
                          {JSON.stringify(event.payload).slice(0, 80)}
                        </p>
                      )}
                      <p className="text-[10px] text-white/20 mt-0.5">
                        {new Date(event.created_at).toLocaleString('ja-JP')}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </motion.div>

          {/* Quick Actions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            className="glass-card"
          >
            <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
              <IconCpu size={16} color="#8b5cf6" />
              クイックアクション
            </h3>

            <div className="space-y-2">
              {[
                { label: 'プロジェクトを作成', path: '/projects', icon: <IconProject size={16} />, color: '#10b981' },
                { label: 'ワークフローを追加', path: '/workflows', icon: <IconWorkflow size={16} />, color: '#3b82f6' },
                { label: 'アクティビティ確認', path: '/activity', icon: <IconActivity size={16} />, color: '#06b6d4' },
              ].map((action) => (
                <button
                  key={action.path}
                  onClick={() => navigate(action.path)}
                  className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-white/5 transition-all group text-left"
                >
                  <div
                    className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 transition-transform group-hover:scale-110"
                    style={{ background: `${action.color}15`, border: `1px solid ${action.color}25` }}
                  >
                    <span style={{ color: action.color }}>{action.icon}</span>
                  </div>
                  <span className="text-sm text-white/60 group-hover:text-white/80 transition-colors">{action.label}</span>
                </button>
              ))}
            </div>

            {/* AI Status */}
            <div className="mt-4 p-3 rounded-xl bg-purple-500/5 border border-purple-500/10">
              <div className="flex items-center gap-2 mb-1">
                <IconCpu size={14} color="#8b5cf6" />
                <span className="text-xs font-medium text-purple-400">AI統合ステータス</span>
              </div>
              <p className="text-[10px] text-white/30">
                ルールベースエンジン稼働中。AI統合はプラグインから有効化可能。
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default Dashboard;
